/**
 * Personal Data Header
 * Displays compact modules for Calendar, Email, and Tasks in the header
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';

interface Task {
  id: number;
  title: string;
  status: string;
  priority: string;
  due_date?: string;
  description?: string;
}

interface Event {
  id: number;
  title: string;
  start_time: string;
  end_time: string;
  location?: string;
  description?: string;
}

interface Email {
  id: number;
  sender: string;
  subject: string;
  body?: string;
  recipient?: string;
  email_type?: string;
  is_read: number;
  is_starred: number;
  received_at: string;
}

interface Knowledge {
  id: number;
  title: string;
  content: string;
  category: string;
  created_at: string;
  updated_at: string;
}

interface Course {
  id: number;
  code: string;
  name: string;
  description: string;
  category: string;
  difficulty: string;
  estimated_hours: number;
  icon: string;
}

interface PersonalDataSummary {
  tasks: {
    pending: Task[];
    in_progress: Task[];
    completed_recent: Task[];
  };
  events: {
    upcoming: Event[];
    today: Event[];
  };
  emails: {
    unread: Email[];
    recent: Email[];
    drafts: Email[];
    sent: Email[];
  };
  knowledge: Knowledge[];
}

// Add props interface
interface PersonalDataHeaderProps {
  onRefreshReady?: (refreshFn: () => void) => void;
}

const PersonalDataHeader: React.FC<PersonalDataHeaderProps> = ({ onRefreshReady }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<PersonalDataSummary | null>(null);
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [courses, setCourses] = useState<Course[]>([]);


  // Fetch personal data summary
  useEffect(() => {
    fetchPersonalData();
    // Expose refresh function to parent
    if (onRefreshReady) {
      onRefreshReady(fetchPersonalData);
    }
    // Refresh every 30 seconds as backup
    const interval = setInterval(fetchPersonalData, 30000);
    return () => clearInterval(interval);
  }, [onRefreshReady]);

  const fetchPersonalData = async () => {
    try {
      const response = await apiClient.get('/personal-data/summary');
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch personal data:', error);
      setLoading(false);
    }
  };

  // Fetch courses for curriculum dropdown
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await apiClient.get('/curriculum/courses');
        setCourses(response.data);
      } catch (error) {
        console.error('Failed to fetch courses:', error);
      }
    };
    fetchCourses();
  }, []);

  // formatDate removed - was used for email/tasks modules (disabled)
  // See commented-out code sections below for reference

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const toggleModule = (module: string) => {
    setActiveModule(activeModule === module ? null : module);
  };

  if (loading) {
    return (
      <div className="personal-data-header">
        <div className="loading-indicator">Loading...</div>
      </div>
    );
  }

  // Tasks and Emails variables removed - modules disabled
  const todayEvents = data?.events?.today || [];
  const upcomingEvents = data?.events?.upcoming || [];
  const knowledgeEntries = data?.knowledge || [];

  // Generate calendar week view
  const generateWeekView = () => {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - currentDay); // Start from Sunday

    const week = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);

      // Get events for this day
      const dayStr = day.toDateString();
      const dayEvents = [...todayEvents, ...upcomingEvents].filter(event => {
        const eventDate = new Date(event.start_time);
        return eventDate.toDateString() === dayStr;
      });

      week.push({
        date: day,
        dayName: day.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNumber: day.getDate(),
        isToday: day.toDateString() === now.toDateString(),
        events: dayEvents
      });
    }
    return week;
  };

  const weekView = generateWeekView();

  // Email and Tasks totals removed - modules disabled
  const totalEvents = todayEvents.length + upcomingEvents.length;
  const totalKnowledge = knowledgeEntries.length;

  return (
    <div className="personal-data-header">
      {/* Calendar Module */}
      <div className="header-module">
        <button
          className={`module-button ${activeModule === 'calendar' ? 'active' : ''}`}
          onClick={() => {
            // On mobile, navigate directly. On desktop, toggle dropdown
            if (window.innerWidth < 768) {
              navigate('/calendar');
            } else {
              toggleModule('calendar');
            }
          }}
          onDoubleClick={() => navigate('/calendar')}
          title="Click to view summary, double-click for full view"
        >
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="module-label">Calendar</span>
          {totalEvents > 0 && (
            <span className="module-badge">{totalEvents}</span>
          )}
        </button>

        {activeModule === 'calendar' && (
          <div className="module-dropdown">
            <div className="dropdown-header">
              <div className="flex justify-between items-center">
                <h3>Calendar - {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
                <button
                  onClick={() => navigate('/calendar')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  View Full Calendar →
                </button>
              </div>
            </div>
            <div className="dropdown-content">
              {/* Week View Calendar Grid */}
              <div className="calendar-grid">
                {weekView.map((day, index) => (
                  <div
                    key={index}
                    className={`calendar-day ${day.isToday ? 'today' : ''}`}
                  >
                    <div className="calendar-day-header">
                      <div className="calendar-day-name">{day.dayName}</div>
                      <div className={`calendar-day-number ${day.isToday ? 'today-number' : ''}`}>
                        {day.dayNumber}
                      </div>
                    </div>
                    <div className="calendar-day-events">
                      {day.events.length > 0 ? (
                        day.events.slice(0, 4).map((event) => (
                          <div key={event.id} className="calendar-event">
                            <div className="calendar-event-time">{formatTime(event.start_time)}</div>
                            <div className="calendar-event-title">{event.title}</div>
                            {event.location && (
                              <div className="calendar-event-location">📍 {event.location}</div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="calendar-no-events">No events</div>
                      )}
                      {day.events.length > 4 && (
                        <div className="calendar-more-events">
                          +{day.events.length - 4} more
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {totalEvents === 0 && (
                <div className="empty-state mt-6">No events scheduled this week</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Curriculum Module */}
      <div className="header-module">
        <button
          className={`module-button ${activeModule === 'curriculum' ? 'active' : ''}`}
          onClick={() => {
            if (window.innerWidth < 768) {
              navigate('/curriculum');
            } else {
              toggleModule('curriculum');
            }
          }}
          onDoubleClick={() => navigate('/curriculum')}
          title="Click to view summary, double-click for full view"
        >
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span className="module-label">Curriculum</span>
        </button>

        {activeModule === 'curriculum' && (
          <div className="module-dropdown">
            <div className="dropdown-header">
              <div className="flex justify-between items-center">
                <h3>Learning Curriculum</h3>
                <button
                  onClick={() => navigate('/curriculum')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  View Full Curriculum →
                </button>
              </div>
            </div>
            <div className="dropdown-content">
              <div className="space-y-3">
                {courses.length > 0 ? (
                  courses.map((course) => (
                    <div
                      key={course.id}
                      className="knowledge-item cursor-pointer hover:bg-blue-50 transition-colors"
                      onClick={() => navigate(`/curriculum?course=${course.code}`)}
                    >
                      <div className="knowledge-header">
                        <div className="knowledge-title">{course.icon} {course.name}</div>
                        <span className="knowledge-category">{course.category}</span>
                      </div>
                      <div className="knowledge-content">{course.description}</div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">Loading courses...</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Exams Module */}
      <div className="header-module">
        <button
          className={`module-button ${activeModule === 'exams' ? 'active' : ''}`}
          onClick={() => navigate('/exams')}
          title="View Exams"
        >
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <span className="module-label">Exams</span>
        </button>
      </div>

      {/* EMAIL AND TASKS MODULES DISABLED - KEPT AS CODE SAMPLES */}
      {/* Email Module - Inbox
      <div className="header-module">
        <button className={`module-button ${activeModule === 'email' ? 'active' : ''}`} onClick={() => toggleModule('email')}>
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="module-label">Inbox</span>
        </button>
      </div>

      Tasks Module
      <div className="header-module">
        <button className={`module-button ${activeModule === 'tasks' ? 'active' : ''}`} onClick={() => toggleModule('tasks')}>
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <span className="module-label">Tasks</span>
        </button>
      </div>
      */}

      {/* Knowledge Base Module */}
      <div className="header-module">
        <button
          className={`module-button ${activeModule === 'knowledge' ? 'active' : ''}`}
          onClick={() => {
            if (window.innerWidth < 768) {
              navigate('/knowledge');
            } else {
              toggleModule('knowledge');
            }
          }}
          onDoubleClick={() => navigate('/knowledge')}
          title="Click to view summary, double-click for full view"
        >
          <svg className="module-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span className="module-label">Knowledge</span>
          {totalKnowledge > 0 && (
            <span className="module-badge">{totalKnowledge}</span>
          )}
        </button>

        {activeModule === 'knowledge' && (
          <div className="module-dropdown">
            <div className="dropdown-header">
              <div className="flex justify-between items-center">
                <h3>Knowledge Base</h3>
                <button
                  onClick={() => navigate('/knowledge')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  View Full Knowledge Base →
                </button>
              </div>
            </div>
            <div className="dropdown-content">
              {knowledgeEntries.length > 0 ? (
                <div className="space-y-3">
                  {knowledgeEntries.map((entry) => (
                    <div key={entry.id} className="knowledge-item">
                      <div className="knowledge-header">
                        <div className="knowledge-title">{entry.title}</div>
                        <span className="knowledge-category">{entry.category}</span>
                      </div>
                      <div className="knowledge-content">{entry.content}</div>
                      <div className="text-xs text-gray-500 mt-2">ID: {entry.id}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No knowledge entries</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalDataHeader;
